<template>
  <div class="email-composer">
    <div class="grid formgrid p-fluid gap-4">
      <!-- Toolbar -->
      <div class="col-12">
        <div class="ml-auto flex justify-content-end">
          <Button
            type="button"
            label="Clear Selection"
            icon="pi pi-times"
            class="p-button-outlined p-button-sm"
            @click="clearSelection"
          />
        </div>
      </div>
      <!-- Only show individual recipient selection when no list is selected -->
      <template v-if="!selectedEmailListId">
        <!-- Organization Selection -->
        <div class="col-12">
          <div class="field">
            <label for="organization" class="block mb-2">Organization</label>
            <Dropdown
              id="organization"
              v-model="selectedOrganizationId"
              :options="organizationList"
              optionLabel="name"
              optionValue="id"
              placeholder="Select organization"
              :filter="true"
              class="w-full"
            />
          </div>
        </div>

        <!-- Person Selection -->
        <div class="col-12">
          <div class="field">
            <label for="person" class="block mb-2">Contact</label>
            <Dropdown
              id="person"
              v-model="selectedPersonId"
              :options="filteredPersonList"
              optionLabel="name"
              optionValue="id"
              placeholder="Select contact"
              :filter="true"
              class="w-full"
            />
          </div>
        </div>
      </template>

      <!-- From Field -->
      <div class="col-12">
        <div class="field">
          <label for="from" class="block mb-2">From</label>
          <InputText
            id="from"
            v-model="email.from"
            :placeholder="companyEmailPlaceholder"
            class="w-full"
          />
        </div>
      </div>

      <!-- To Field -->
      <div class="col-12">
        <div class="field">
          <label for="to" class="block mb-2">To</label>
          <InputText
            id="to"
            v-model="recipientEmail"
            placeholder="Recipient email address"
            class="w-full"
            :disabled="!!recipientEmail"
          />
          <small v-if="selectedPersonId" class="block mt-1 text-xs text-gray-500">
            Email address is set from the selected contact
          </small>
        </div>
      </div>

      <!-- Template Selection -->
      <div class="col-12">
        <div class="field">
          <label for="template" class="block mb-2">Email Template</label>
          <Dropdown
            id="template"
            v-model="selectedTemplateId"
            :options="templateList"
            optionLabel="name"
            optionValue="id"
            placeholder="Select template"
            :filter="true"
            @change="onTemplateChange"
            class="w-full"
          />
        </div>
      </div>

      <div class="col-12">
        <div class="field">
          <label for="subject" class="block mb-2">Subject</label>
          <InputText id="subject" v-model="email.message.subject" class="w-full text-xl" />
        </div>
      </div>

      <div class="col-12">
        <div class="field">
          <label for="body" class="block mb-2">Message</label>
          <div v-if="compileError" class="mb-2 text-red-600 text-sm">
            Template error: {{ compileError }}
          </div>
          <RichTextEditor
            v-model="editorHtml"
            :showToolbar="true"
            @update:modelValue="onEditorUpdate"
          />
        </div>
      </div>

      <div class="col-12">
        <div class="ml-auto flex gap-2 flex-column md:flex-row">
          <Button
            type="button"
            label="Preview Email"
            icon="pi pi-eye"
            class="ml-auto p-button-outlined w-full md:w-auto"
            @click="showPreviewDialog = true"
          />
          <Button
            :label="recipientCount > 1 ? `Send Email (${recipientCount} recipients)` : 'Send Email'"
            icon="pi pi-send"
            class="w-full md:w-auto p-button-lg"
            :disabled="!canSend || isSending"
            :loading="isSending"
            @click="sendEmail"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Preview Dialog -->
  <Dialog
    v-model:visible="showPreviewDialog"
    modal
    header="Email Preview"
    :style="{ width: '800px' }"
  >
    <div class="email-preview">
      <div class="preview-header">
        <div class="mb-2"><strong>From:</strong> {{ email.from || companyEmailPlaceholder }}</div>
        <div class="mb-2">
          <strong>To:</strong> {{ recipientEmail }}
          <span v-if="recipientCount > 1" class="text-sm text-gray-600 ml-2">
            ({{ recipientCount }} recipients)
          </span>
        </div>
        <div><strong>Subject:</strong> {{ email.message.subject }}</div>
      </div>
      <div v-if="compileError" class="mb-3 text-red-600 text-sm">
        Template error: {{ compileError }}. Click "Edit Message" to fix or adjust data.
      </div>
      <div v-else-if="unresolvedPlaceholders.length" class="mb-3 text-red-600 text-sm">
        Unresolved placeholders: {{ unresolvedPlaceholders.join(', ') }}. Click "Edit Message" to
        fix.
      </div>
      <div class="preview-body" v-html="email.message.html"></div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, shallowRef, computed, watch, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import Dialog from 'primevue/dialog'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import RichTextEditor from '@/components/common/RichTextEditor.vue'
import Handlebars from 'handlebars'

// Register Handlebars helpers (guarded for HMR)
if (!(Handlebars as any).helpers?.default) {
  Handlebars.registerHelper('default', function (value: unknown, fallback: string) {
    if (value === undefined || value === null || value === '') {
      return fallback
    }
    return value as any
  })
}

import { usePersonStore } from '@/stores/v2/person'
import { useOrganizationStore } from '@/stores/v2/organization'
import type { Person } from '@/types/Person'
import { useEmailTemplateStore } from '@/stores/emailTemplateStore'
import { useEmailService } from '@/services/emailService'
import { createEmail } from '@/factories/emailFactory'
import { type EmailMessage } from '@/types/EmailMessage'
import { type EmailTemplate } from '@/types/EmailTemplate'
import { useEmailListStore } from '@/stores/emailListStore'
import { useEmailSubscriptionStore } from '@/stores/emailSubscriptionStore'
import { useSettingsStore } from '@/stores/settings'
import type { EmailList } from '@/types/email'

const props = defineProps<{
  organizationId?: string
  personId?: string
  emailListId?: string
}>()

const emit = defineEmits<{
  (e: 'sent', emailLogId: string): void
  (e: 'error', error: Error): void
  (e: 'cancel'): void
}>()

const toast = useToast()

// Ensure we have a valid email message structure
const defaultEmailMessage: EmailMessage = createEmail({})

// State
const selectedOrganizationId = ref<string | null>(props.organizationId || null)
const selectedPersonId = ref<string | null>(props.personId || null)
const selectedTemplateId = ref<string | null>(null)
const isSending = ref(false)
const compileError = ref<string | null>(null)
const showPreviewDialog = ref(false)
const organizationOptions = ref<{ id: string; name: string; email: string | undefined }[]>([])
const personOptions = ref<{ id: string; name: string; email: string; organizationId?: string }[]>(
  [],
)
const selectedEmailListId = ref<string | null>(props.emailListId || null)
const emailListOptions = ref<EmailList[]>([])
const templateData = ref<Record<string, unknown>>({})
const selectedTemplateHtmlBase = ref<string>('')
const selectedTemplateSubjectBase = ref<string>('')
const isOrgChangeFromPerson = ref(false)

// Editor v-model expects string; coerce undefined to '' and sync back
const editorHtml = computed<string>({
  get() {
    return email.value.message.html || ''
  },
  set(value: string) {
    email.value = {
      ...email.value,
      message: {
        ...email.value.message,
        html: value,
      },
    }
  },
})

// Direct reactive state for email
const email = shallowRef<EmailMessage>({
  ...defaultEmailMessage,
  personId: props.personId,
  organizationId: props.organizationId,
  emailListId: props.emailListId,
})

// Stores
const personStore = usePersonStore()
const organizationStore = useOrganizationStore()
const emailTemplateStore = useEmailTemplateStore()
const emailService = useEmailService()
const emailListStore = useEmailListStore()
const emailSubscriptionStore = useEmailSubscriptionStore()
const settingsStore = useSettingsStore()

// Load organization and person options
async function loadOrganizationOptions() {
  organizationOptions.value = await emailService.getEmailableOrganizations()
}

async function loadPersonOptions() {
  personOptions.value = await emailService.getEmailablePeople(
    undefined,
    selectedOrganizationId.value || undefined,
  )
}

// Load email list options
async function loadEmailLists() {
  await emailListStore.load()
  emailListOptions.value = emailListStore.items
}

// Computed
const organizationList = computed(() => organizationOptions.value)
const filteredPersonList = computed(() => personOptions.value)
const companyEmailPlaceholder = computed(() => settingsStore.companySettings.email || '')

const templateList = computed(() => {
  console.log('All templates:', emailTemplateStore.items)
  return emailTemplateStore.items.map((template: EmailTemplate) => {
    console.log('Processing template:', template)
    return {
      id: template.id,
      name: `${template.name} - ${template.subject}`,
    }
  })
})

// Computed property for recipient display
const recipientEmail = computed(() => {
  if (selectedEmailListId.value) {
    // For display purposes only, show all recipients
    const recipients = Array.isArray(email.value.to) ? email.value.to : [email.value.to]
    return recipients.filter(Boolean).join(', ')
  }
  // Single recipient mode
  return (Array.isArray(email.value.to) ? email.value.to[0] : email.value.to) || ''
})

// Add a computed property for recipient count
const recipientCount = computed(() => {
  if (!email.value.to) return 0
  return Array.isArray(email.value.to) ? email.value.to.filter(Boolean).length : 1
})

// Selected person entity (fetched individually; avoids relying on store.items)
const selectedPerson = ref<Person | null>(null)
watch(
  () => selectedPersonId.value,
  async (id) => {
    selectedPerson.value = id ? await personStore.get(id) : null
  },
  { immediate: true },
)

// Predefined template variables (computed)
const templateVars = computed<Record<string, unknown>>(() => {
  const vars: Record<string, unknown> = { ...templateData.value }
  const p = selectedPerson.value || undefined
  if (p?.firstName) {
    vars.greeting = `Hi ${p.firstName}`
  }
  return vars
})

// Handlebars strict compile helper
function compileWithHandlebars(template: string, data: Record<string, unknown>): string | null {
  try {
    console.log('[EmailComposer] Compiling template (strict)')
    const compiled = Handlebars.compile(template, { strict: true })
    const output = compiled(data)
    console.log('[EmailComposer] Compile success, output length:', output?.length)
    return output
  } catch (err) {
    compileError.value = err instanceof Error ? err.message : String(err)
    console.error('[EmailComposer] Compile error:', compileError.value)
    return null
  }
}

const unresolvedPlaceholders = computed<string[]>(() => {
  const html = email.value.message.html || ''
  const matches = html.match(/\{\{[^}]+\}\}/g) || []
  return Array.from(new Set(matches))
})

// Update the canSend computed property
const canSend = computed(() => {
  const hasRecipients = Array.isArray(email.value.to)
    ? email.value.to.some(Boolean)
    : Boolean(email.value.to?.trim())

  return (
    hasRecipients &&
    email.value.message.subject?.trim() &&
    email.value.message.html?.trim() &&
    unresolvedPlaceholders.value.length === 0 &&
    !compileError.value
  )
})

// Re-check compile errors on editor updates without mutating content
function onEditorUpdate(newValue: string) {
  compileError.value = null
  const output = compileWithHandlebars(newValue || '', templateVars.value)
  if (output !== null && output !== email.value.message.html) {
    email.value = {
      ...email.value,
      message: {
        ...email.value.message,
        html: output,
      },
    }
  }
}

// Methods
function onTemplateChange() {
  if (!selectedTemplateId.value) {
    // If template is cleared, reset to empty content
    email.value = {
      ...email.value,
      message: {
        ...email.value.message,
        subject: '',
        html: '',
      },
      meta: undefined,
    }
    templateData.value = {}
    selectedTemplateHtmlBase.value = ''
    selectedTemplateSubjectBase.value = ''
    return
  }

  const template = emailTemplateStore.items.find(
    (t: EmailTemplate) => t.id === selectedTemplateId.value,
  )

  if (template) {
    compileError.value = null
    console.log('[EmailComposer] onTemplateChange: selected template', {
      id: template.id,
      name: template.name,
    })
    selectedTemplateHtmlBase.value = template.html || ''
    selectedTemplateSubjectBase.value = template.subject || ''
    const compiledHtml = compileWithHandlebars(selectedTemplateHtmlBase.value, templateVars.value)
    const compiledSubject = compileWithHandlebars(
      selectedTemplateSubjectBase.value,
      templateVars.value,
    )
    email.value = {
      ...email.value,
      message: {
        ...email.value.message,
        subject: compiledSubject ?? selectedTemplateSubjectBase.value,
        html: compiledHtml ?? selectedTemplateHtmlBase.value,
      },
      meta: {
        selectedTemplateId: template.id,
        selectedTemplateName: template.name,
        data: templateVars.value,
      },
    }
    console.log('[EmailComposer] onTemplateChange: updated message', {
      subjectPreview: email.value.message.subject?.slice(0, 120),
      htmlPreview: email.value.message.html?.slice(0, 120),
      compileError: compileError.value,
    })
  }
}

// Recompile from the original template strings using current data
function recompileFromBase() {
  if (!selectedTemplateId.value) return
  const compiledHtml = compileWithHandlebars(
    selectedTemplateHtmlBase.value || '',
    templateVars.value,
  )
  const compiledSubject = compileWithHandlebars(
    selectedTemplateSubjectBase.value || '',
    templateVars.value,
  )
  email.value = {
    ...email.value,
    message: {
      ...email.value.message,
      subject: compiledSubject ?? selectedTemplateSubjectBase.value,
      html: compiledHtml ?? selectedTemplateHtmlBase.value,
    },
    meta: {
      ...(email.value.meta || {}),
      data: templateVars.value,
    },
  }
}

// Clear selected organization and person (and recipient in single-recipient mode)
function clearSelection() {
  selectedOrganizationId.value = null
  selectedPersonId.value = null
  if (!selectedEmailListId.value) {
    email.value = {
      ...email.value,
      to: '',
      organizationId: undefined,
      personId: undefined,
    }
  }
}

async function sendEmail() {
  if (!email.value.to) return

  try {
    isSending.value = true
    let emailLogIds: string[] = []

    // Create base email without recipient info
    const baseEmail = {
      ...email.value,
      // Ensure template attribute is not sent; use meta instead
      template: undefined,
      meta: {
        selectedTemplateId: selectedTemplateId.value || undefined,
        selectedTemplateName:
          (selectedTemplateId.value
            ? emailTemplateStore.items.find((t: EmailTemplate) => t.id === selectedTemplateId.value)
                ?.name
            : undefined) || undefined,
        data: templateData.value,
      },
      to: undefined, // Will be set by service
      personId: undefined,
      organizationId: undefined,
    }

    if (selectedEmailListId.value) {
      // Send to list
      emailLogIds = await emailService.sendEmailToList(baseEmail, selectedEmailListId.value)
    } else {
      // Send to single recipient
      const recipient = {
        email: Array.isArray(email.value.to) ? email.value.to[0] : email.value.to,
        personId: selectedPersonId.value || undefined,
        organizationId: selectedOrganizationId.value || undefined,
      }

      emailLogIds = [
        await emailService.sendEmail({
          ...baseEmail,
          to: recipient.email,
          personId: recipient.personId,
          organizationId: recipient.organizationId,
        }),
      ]
    }

    toast.add({
      severity: 'success',
      summary: 'Email sent',
      detail: `Sent to ${emailLogIds.length} recipient${emailLogIds.length > 1 ? 's' : ''}`,
      life: 3000,
    })

    selectedOrganizationId.value = null
    selectedPersonId.value = null
    selectedEmailListId.value = null
    email.value.to = ''

    // Emit all email log IDs
    emit('sent', emailLogIds.join(','))
  } catch (error) {
    console.error('Failed to send email:', error)
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
    toast.add({
      severity: 'error',
      summary: 'Failed to send email',
      detail: errorMessage,
      life: 5000,
    })
    emit('error', error instanceof Error ? error : new Error(errorMessage))
  } finally {
    isSending.value = false
  }
}

// Watch props changes to update local state
watch(
  () => props.organizationId,
  async (newId) => {
    if (newId) {
      selectedOrganizationId.value = newId
      // Only update organization email if no person is selected
      if (!props.personId) {
        // Load organization options if not already loaded
        if (organizationOptions.value.length === 0) {
          await loadOrganizationOptions()
        }
        // Update recipient email for organization
        const organization = organizationOptions.value.find((org) => org.id === newId)
        if (organization?.email) {
          email.value = {
            ...email.value,
            to: organization.email,
            organizationId: newId,
            personId: undefined,
          }
        }
      }
    }
  },
  { immediate: true },
)

watch(
  () => props.personId,
  async (newId) => {
    if (newId) {
      selectedPersonId.value = newId
      // Load person options if not already loaded
      if (personOptions.value.length === 0) {
        await loadPersonOptions()
      }
      // Update recipient email for person
      const person = personOptions.value.find((p) => p.id === newId)
      if (person?.email) {
        email.value = {
          ...email.value,
          to: person.email,
          personId: newId,
          organizationId: selectedOrganizationId.value || undefined,
        }
        // Recompile with updated person
        recompileFromBase()
      }
    } else {
      // If person is cleared, fall back to organization email
      if (props.organizationId) {
        const organization = organizationOptions.value.find(
          (org) => org.id === props.organizationId,
        )
        if (organization?.email) {
          email.value = {
            ...email.value,
            to: organization.email,
            organizationId: props.organizationId,
            personId: undefined,
          }
          // Recompile with updated organization
          recompileFromBase()
        }
      }
    }
  },
  { immediate: true },
)

// Watch for organization changes to reload person options and update email
watch(selectedOrganizationId, async (newId) => {
  await loadPersonOptions()

  if (isOrgChangeFromPerson.value) {
    // Organization was updated due to selecting a person; do not clear person
    isOrgChangeFromPerson.value = false
  } else {
    // Clear selected person when organization changes directly
    selectedPersonId.value = null
    // Clear current recipient in single-recipient mode
    if (!selectedEmailListId.value) {
      email.value = {
        ...email.value,
        to: '',
      }
    }
  }

  // Update recipient email if no person is selected
  if (newId && !selectedPersonId.value) {
    const organization = organizationOptions.value.find((org) => org.id === newId)
    if (organization?.email) {
      email.value = {
        ...email.value,
        to: organization.email,
      }
      // Recompile with updated organization
      recompileFromBase()
    }
  }
})

// Watch for person selection to update recipient email
watch(selectedPersonId, (newId) => {
  if (newId) {
    const person = personOptions.value.find((p) => p.id === newId)
    if (person?.email) {
      // Set the email recipient to the selected person
      email.value = {
        ...email.value,
        to: person.email,
      }

      // Update organization if needed
      if (person.organizationId && person.organizationId !== selectedOrganizationId.value) {
        isOrgChangeFromPerson.value = true
        selectedOrganizationId.value = person.organizationId
      }
      // Recompile with updated person
      recompileFromBase()
    }
  }
})

// Watch local state to update email
watch([selectedOrganizationId, selectedPersonId], ([newOrgId, newPersonId]) => {
  email.value = {
    ...email.value,
    organizationId: newOrgId || undefined,
    personId: newPersonId || undefined,
  }
  // Recompile when either person or organization changes
  recompileFromBase()
})

// Watch for template store loading to initialize template selection
watch(
  () => emailTemplateStore.items,
  (templates) => {
    if (templates.length > 0) {
      // No need to initialize template from props anymore
      // since we're not using modelValue
    }
  },
  { immediate: true },
)

// Watch for email list selection
watch(
  () => props.emailListId,
  async (newId) => {
    if (newId) {
      // Load subscribers for the selected list
      const subscriptions = await emailSubscriptionStore.query([
        { field: 'listId', op: '==', value: newId },
        { field: 'status', op: '==', value: 'active' },
      ])

      // Update email recipients
      email.value = {
        ...email.value,
        to: subscriptions.map((sub) => sub.email),
        emailListId: newId,
      }
    } else {
      // Reset to single recipient mode
      email.value = {
        ...email.value,
        to: Array.isArray(email.value.to) ? email.value.to[0] || '' : email.value.to,
        emailListId: undefined,
      }
    }
  },
  { immediate: true },
)

// Lifecycle
onMounted(async () => {
  console.log('email', email.value)

  // Load initial data
  await Promise.all([
    loadOrganizationOptions(),
    loadPersonOptions(),
    loadEmailLists(),
    emailTemplateStore.load(),
  ])

  // If we have a personId in the props, select it
  if (props.personId) {
    selectedPersonId.value = props.personId
  }

  // If we have an organizationId in the props, select it
  if (props.organizationId) {
    selectedOrganizationId.value = props.organizationId
  }

  // If we have an emailListId in the props, select it
  if (props.emailListId) {
    selectedEmailListId.value = props.emailListId
  }
})
</script>

<style scoped>
.email-composer {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

.field {
  margin-bottom: 1.5rem;
}

.preview-card {
  margin: 1rem 0;
}

.email-preview {
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 1.5rem;
  background-color: var(--surface-card);
}

.preview-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--surface-border);
  font-size: 1.1rem;
}

.preview-body {
  min-height: 300px;
  line-height: 1.6;
}

:deep(.p-dropdown) {
  width: 100%;
}

:deep(.p-inputtext) {
  font-size: 1rem;
  padding: 0.75rem;
}

:deep(.p-button) {
  padding: 0.75rem 1.5rem;
}

@media screen and (max-width: 768px) {
  .email-composer {
    padding: 0.5rem;
  }

  .field {
    margin-bottom: 1rem;
  }

  .preview-body {
    min-height: 200px;
  }
}
</style>
